import { GridValidRowModel } from '@mui/x-data-grid-premium';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { useSnackbar } from 'notistack';
import React, { PropsWithChildren, createContext, useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useColumns } from 'shared/hooks/useColumns';
import { useTableDataRequest } from 'shared/hooks/useTableDataRequest';
import { GridColumns, TableDataContext } from 'shared/types';
import { toBackendFilterModel } from 'shared/utility/dataGrid';
import { RowEditRequest, RowEditResponse, SoftEditRequest, SoftEditResponse, TableDataResponse } from 'types/backend';
import { useHttp } from './provide-http';

export function createTableDataContext<T>() {
    return createContext<TableDataContext<T>>({} as TableDataContext<T>);
}

type ProvideTableDataProps<T> = {
    name: string;
    tableDataContext: React.Context<TableDataContext<T>>;
    alternateQueryPath?: string;
    config?: {
        initialState?: GridInitialStatePremium;
        persistentInitialState?: GridInitialStatePremium;
        customFunctions?: {
            onRowEditSuccess: (rowEditRequest: RowEditRequest<T>) => Promise<void>;
        };
    };
};

export function ProvideTableData<T extends { id: string }>({
    children,
    name,
    tableDataContext,
    alternateQueryPath,
    config,
}: PropsWithChildren<ProvideTableDataProps<T>>) {
    const { debouncedTableDataRequest, dontDebounceTableDataRequest, serverSideProps, setTableDataRequest } =
        useTableDataRequest({
            initialTableDataRequest: {
                FilterModel: config?.initialState?.filter?.filterModel
                    ? toBackendFilterModel(config.initialState.filter.filterModel)
                    : undefined,
                PageNumber: config?.initialState?.pagination?.paginationModel?.page || 0,
                PageSize: config?.initialState?.pagination?.paginationModel?.pageSize || 15,
                SortModel: config?.initialState?.sorting?.sortModel,
            },
            persistentInitialTableDataRequest: {
                FilterModel: config?.persistentInitialState?.filter?.filterModel
                    ? toBackendFilterModel(config.persistentInitialState.filter.filterModel)
                    : undefined,
                PageNumber: config?.initialState?.pagination?.paginationModel?.page || 0,
                PageSize: config?.initialState?.pagination?.paginationModel?.pageSize || 15,
                SortModel: config?.initialState?.sorting?.sortModel,
            },
        });
    const queryClient = useQueryClient();

    const { enqueueSnackbar } = useSnackbar();
    const http = useHttp();

    const response = useQuery<TableDataResponse<T>, Error>(
        [name, debouncedTableDataRequest, alternateQueryPath],
        async () => {
            const { data } = await http.post(
                `api/${name}/${alternateQueryPath ? alternateQueryPath : 'subset'}`,
                debouncedTableDataRequest
            );
            return data || [];
        },
        {
            onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const sendSoftEditMutation = useMutation<SoftEditResponse<T>, Error, SoftEditRequest<T>>(
        async (softEditRequest) => {
            const { data } = await http.put(`api/${name}`, softEditRequest);
            return data;
        },
        {
            onSuccess: (data) => {
                const newResponse = response.data?.rows.map((row) => {
                    if (row.id === data.prevId) return data.vwObject;
                    return row;
                });
                queryClient.setQueryData([name, debouncedTableDataRequest], {
                    rows: newResponse,
                    totalRows: response.data?.totalRows,
                });

                enqueueSnackbar('Edit Successful', { variant: 'success' });
            },
            onError: (error) => {
                enqueueSnackbar(error.message, { variant: 'error' });
            },
        }
    );

    const sendExportMutation = useMutation<void, Error>(
        async () => {
            const { data, headers } = await http.post<Blob>(`api/${name}/export`, {
                ...debouncedTableDataRequest,
                headers: {
                    'Content-Type': 'text/csv',
                },
                responseType: 'blob',
            });
            const fileName = headers['content-disposition']?.split('=')[1] || `${name}Export.csv`;

            handleBlob(data, fileName);
        },
        {
            onSuccess: () => {
                enqueueSnackbar('Export Successful', { variant: 'success' });
            },
            onError: (error) => {
                enqueueSnackbar(error.message, { variant: 'error' });
            },
        }
    );

    const sendSoftEditHandler = (newRow: T, oldRow: T) => {
        if (!oldRow) return newRow;

        if (JSON.stringify(oldRow) === JSON.stringify(newRow)) return newRow;

        const softEditRequest = { vwObject: newRow };
        sendSoftEditMutation.mutateAsync(softEditRequest);
        return newRow;
    };

    const sendRowEditMutation = useMutation<
        { data: RowEditResponse; rowEditRequest: RowEditRequest<T> },
        Error,
        RowEditRequest<T>
    >(
        async (rowEditRequest) => {
            const { data } = await http.put(`api/${name}`, rowEditRequest);
            return { data, rowEditRequest };
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries([name, debouncedTableDataRequest, alternateQueryPath]);
                if (config?.customFunctions?.onRowEditSuccess) {
                    config.customFunctions.onRowEditSuccess(data.rowEditRequest);
                }
                enqueueSnackbar('Edit Successful', { variant: 'success' });
            },
            onError: (error) => {
                enqueueSnackbar(error.message, { variant: 'error' });
            },
        }
    );

    const sendRowEdit = (newRow: T, oldRow: T) => {
        if (!oldRow) return newRow;

        if (JSON.stringify(oldRow) === JSON.stringify(newRow)) return newRow;

        const rowEditRequest = { vwObject: newRow };
        sendRowEditMutation.mutateAsync(rowEditRequest);
        return newRow;
    };

    const sendRowDeleteMutation = useMutation<RowEditResponse, Error, string>(
        async (currentPriceUIId) => {
            const { data } = await http.put(`api/${name}/deleteRow?currentPriceUIId=${currentPriceUIId}`);
            return data;
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries([name, debouncedTableDataRequest, alternateQueryPath]);

                enqueueSnackbar('Delete Successful', { variant: 'success' });
            },
            onError: (error) => {
                enqueueSnackbar(error.message, { variant: 'error' });
            },
        }
    );

    const sendRowDelete = (newRow: T) => {
        const currentPriceUIId = newRow.id;
        sendRowDeleteMutation.mutateAsync(currentPriceUIId);
        return newRow;
    };

    const getExportHandler = () => {
        return sendExportMutation.mutateAsync();
    };

    const [isMassActionModalopen, setIsMassActionModalopen] = useState(false);
    const handleMassActionModalOpen = () => setIsMassActionModalopen(true);
    const handleMassActionModalClose = () => setIsMassActionModalopen(false);

    return (
        <tableDataContext.Provider
            value={{
                response,
                body: debouncedTableDataRequest,
                setBody: setTableDataRequest,
                dontDebounceTableDataRequest,
                serverSideProps,
                sendSoftEditHandler,
                getExportHandler,
                sendRowEdit,
                sendRowDelete,
                filtersApplied: debouncedTableDataRequest.FilterModel,
                isRowEditLoading: sendRowEditMutation.isLoading,
                isRowDeleteLoading: sendRowDeleteMutation.isLoading,
                isExportLoading: sendExportMutation.isLoading,
                refreshData: () => {
                    queryClient.invalidateQueries([name, debouncedTableDataRequest, alternateQueryPath]);
                },
                massActionModal: {
                    isMassActionModalopen,
                    handleMassActionModalOpen,
                    handleMassActionModalClose,
                },
            }}
        >
            {children}
        </tableDataContext.Provider>
    );
}

export function useTableData<T extends GridValidRowModel>(
    tableDataContext: React.Context<TableDataContext<T>>,
    columnConfig: GridColumns<T>
) {
    const context = useContext(tableDataContext);

    const columns = useColumns<T>({ columnConfig });

    // useEffect(() => {
    //     setBody(debouncedTableDataRequest);
    // }, [debouncedTableDataRequest, setBody]);

    // useEffect(() => {
    //     const sortModel = config?.initialState?.sorting?.sortModel;
    //     setBody((previousBody) => ({ ...previousBody, SortModel: sortModel }));
    //     setTableDataRequest((previousTDR) => ({ ...previousTDR, SortModel: sortModel }));
    // }, [config?.initialState?.sorting?.sortModel, setBody, setTableDataRequest]);

    return {
        dataGridProProps: {
            rows: context.response?.data?.rows || [],
            rowCount: context.response?.data?.totalRows || 0,
            loading: !!(context.response?.isLoading || context.response?.isFetching),
            columns,
            processRowUpdate: context.sendSoftEditHandler,
            ...context.serverSideProps,
        },
        dataGridProManualSaveProps: {
            processRowUpdate: context.sendRowEdit,
        },
        getExportHandler: context.getExportHandler,
        isExportLoading: context.isExportLoading,
    };
}

export function handleBlob(blob: Blob | string, fileName: string) {
    const blobUrl = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    link.click();
}
